import { useState } from "react";
import { Mail, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { currentLang } from "@/lib/lang";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function NewsletterSignup({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | busy | success | already
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("busy");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: form.email,
        name: form.name || "",
        language: currentLang(),
      });
      setMessage(data.message || "You're on the list.");
      setStatus(data.status === "already_subscribed" ? "already" : "success");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  if (status === "success" || status === "already") {
    return (
      <div
        data-testid="newsletter-success"
        className={`border border-gold-500/30 bg-ink-paper/40 ${compact ? "p-6" : "p-10 md:p-14"} text-center`}
      >
        <CheckCircle2 size={compact ? 32 : 48} className="text-gold-500 mx-auto mb-4" strokeWidth={1.5} />
        <p className={`font-serif ${compact ? "text-xl" : "text-2xl md:text-3xl"} text-white`}>
          {status === "already" ? "You're already with us" : "Welcome to the family"}
        </p>
        <p className="mt-3 text-sm text-white/60">{message}</p>
      </div>
    );
  }

  return (
    <div
      data-testid="newsletter-signup"
      className={`border border-gold-500/25 bg-ink-paper/40 ${compact ? "p-6" : "p-8 md:p-12"}`}
    >
      <div className="flex items-center gap-3">
        <Mail size={compact ? 16 : 20} className="text-gold-500" />
        <span className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Newsletter</span>
      </div>
      <h3 className={`mt-4 font-serif ${compact ? "text-2xl" : "text-3xl md:text-4xl"} text-white leading-tight`}>
        Weekly Kingdom insights, straight to your inbox.
      </h3>
      <p className="mt-3 text-sm md:text-base text-white/60 max-w-xl">
        Devotionals, teaching, upcoming events, and new AI Academy releases. No spam — just fresh manna.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-3 md:space-y-0 md:flex md:gap-3" data-testid="newsletter-form">
        <Input
          type="text"
          placeholder="Your name (optional)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="newsletter-name"
          className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 md:max-w-[220px]"
        />
        <Input
          required
          type="email"
          placeholder="Your email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="newsletter-email"
          className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 md:flex-1"
        />
        <Button
          type="submit"
          disabled={status === "busy"}
          data-testid="newsletter-submit"
          className="bg-gold-500 text-ink hover:bg-gold-400 font-semibold md:px-6"
        >
          <Send size={14} className="mr-1.5" />
          {status === "busy" ? "Joining…" : "Subscribe"}
        </Button>
      </form>
    </div>
  );
}
